/** Editor для Звіту виробництва за зміну (Порція 4b).
 *
 * Розщеплені permissions (на відміну від інших editors):
 * - Save/Update = production.report.create
 * - Post/Unpost = production.report.post
 * - Delete = production.report.delete
 *
 * Multi-product 1A — НЕ блокуємо локально, backend POST → 422 з detail
 * (рішення власника: backend authoritative).
 *
 * 2 таблиці: products + materials.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import { nomenclatureGroups } from '@/api/nomenclatureGroups';
import { accounts as accountsApi } from '@/api/documents';
import {
  productionReports,
  type ProductionReportProductIn,
  type ProductionReportMaterialIn,
  type ProductionReportReturnableWasteIn,
} from '@/api/productionReports';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { usePermissions } from '@/auth/usePermissions';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DatePopover from '@/components/DatePopover';

const PERMISSION_CREATE = 'production.report.create';
const PERMISSION_POST = 'production.report.post';
const PERMISSION_DELETE = 'production.report.delete';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  department_id: string;
  comment: string;
  products: ProductionReportProductIn[];
  materials: ProductionReportMaterialIn[];
  returnable_waste: ProductionReportReturnableWasteIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  department_id: '',
  comment: '',
  products: [],
  materials: [],
  returnable_waste: [],
};

const newProduct = (n: number): ProductionReportProductIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});
const newMaterial = (n: number): ProductionReportMaterialIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});
const newWaste = (n: number): ProductionReportReturnableWasteIn => ({
  line_number: n, nomenclature_id: '', quantity: '0', amount: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    // Pydantic 422: detail — array {type, loc, msg, input, ctx}
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}

export default function ProductionReportEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION_CREATE);
  // Розщеплені permissions: save=create, post/unpost=post, delete=delete.
  const { has } = usePermissions();
  const canCreate = has(PERMISSION_CREATE);
  const canPost = has(PERMISSION_POST);
  const canDelete = has(PERMISSION_DELETE);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const groupQ = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: () => nomenclatureGroups.list(),
  });
  // План рахунків для вибору (щоб не вписувати руками — без помилок).
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const [tab, setTab] = useState<'products' | 'materials' | 'waste'>('products');
  const docQuery = useQuery({
    queryKey: ['production-report', id],
    queryFn: () => productionReports.get(id!),
    enabled: !isNew,
  });
  const numberPreview = useNumberPreview('doc.production_reports', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        department_id: d.department_id ?? '',
        comment: d.comment ?? '',
        products: d.products.map((p) => ({
          line_number: p.line_number,
          nomenclature_id: p.nomenclature_id,
          quantity: p.quantity,
          specification_id: p.specification_id,
          account_code: p.account_code,
          cost_account_code: p.cost_account_code,
          planned_cost: p.planned_cost,
          planned_amount: p.planned_amount,
          nomenclature_group_id: p.nomenclature_group_id,
        })),
        materials: d.materials.map((m) => ({
          line_number: m.line_number,
          nomenclature_id: m.nomenclature_id,
          quantity: m.quantity,
          account_code: m.account_code,
        })),
        returnable_waste: (d.returnable_waste ?? []).map((w) => ({
          line_number: w.line_number,
          nomenclature_id: w.nomenclature_id,
          quantity: w.quantity,
          price: w.price,
          amount: w.amount,
          account_code: w.account_code,
          nomenclature_group_id: w.nomenclature_group_id,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;
  // Режим обліку виробництва обраної орг: у Спрощеному ховаємо планові колонки.
  const selectedOrg = orgList.data?.items.find((o) => o.id === form.organization_id);
  const isWip = selectedOrg?.production_costing_mode === '1C_WIP';

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    warehouse_id: form.warehouse_id,
    department_id: form.department_id || null,
    comment: form.comment || null,
    products: form.products,
    materials: form.materials,
    returnable_waste: form.returnable_waste,
  });

  const create = useMutation({
    mutationFn: () => productionReports.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-reports'] });
      navigate(`/production-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => productionReports.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-report', id] });
      qc.invalidateQueries({ queryKey: ['production-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await productionReports.create({ organization_id: form.organization_id, ...buildPayload() });
      await productionReports.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-reports'] });
      navigate(`/production-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await productionReports.update(id!, buildPayload());
      await productionReports.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-report', id] });
      qc.invalidateQueries({ queryKey: ['production-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => productionReports.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-report', id] });
      qc.invalidateQueries({ queryKey: ['production-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => productionReports.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-reports'] });
      navigate('/production-reports');
    },
    onError: (e) => setError(extractError(e)),
  });

  function addProduct() { setForm((f) => ({ ...f, products: [...f.products, newProduct(f.products.length + 1)] })); }
  function removeProduct(i: number) {
    setForm((f) => ({
      ...f,
      products: f.products.filter((_, idx) => idx !== i).map((p, idx) => ({ ...p, line_number: idx + 1 })),
    }));
  }
  function setProductField(i: number, patch: Partial<ProductionReportProductIn>) {
    setForm((f) => ({ ...f, products: f.products.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) }));
  }
  function addMaterial() { setForm((f) => ({ ...f, materials: [...f.materials, newMaterial(f.materials.length + 1)] })); }
  function removeMaterial(i: number) {
    setForm((f) => ({
      ...f,
      materials: f.materials.filter((_, idx) => idx !== i).map((m, idx) => ({ ...m, line_number: idx + 1 })),
    }));
  }
  function setMaterialField(i: number, patch: Partial<ProductionReportMaterialIn>) {
    setForm((f) => ({ ...f, materials: f.materials.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) }));
  }
  function addWaste() { setForm((f) => ({ ...f, returnable_waste: [...f.returnable_waste, newWaste(f.returnable_waste.length + 1)] })); }
  function removeWaste(i: number) {
    setForm((f) => ({
      ...f,
      returnable_waste: f.returnable_waste.filter((_, idx) => idx !== i).map((w, idx) => ({ ...w, line_number: idx + 1 })),
    }));
  }
  function setWasteField(i: number, patch: Partial<ProductionReportReturnableWasteIn>) {
    setForm((f) => ({ ...f, returnable_waste: f.returnable_waste.map((w, idx) => (idx === i ? { ...w, ...patch } : w)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  const tblStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
  const theadStyle: CSSProperties = { background: '#f8fafc', textAlign: 'left' };
  const rowStyle: CSSProperties = { borderTop: '1px solid #e2e8f0' };
  const td: CSSProperties = { padding: '0.5rem' };
  const tabBtn = (active: boolean): CSSProperties => ({
    padding: '0.4rem 0.9rem', border: 'none', background: 'transparent', cursor: 'pointer',
    marginBottom: -2, fontWeight: active ? 600 : 400, color: active ? '#2563eb' : '#334155',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  });
  // Вибір рахунка з плану рахунків (замість ручного вводу — без помилок).
  const acctList = accountsQ.data ?? [];
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}
            disabled={isPosted} style={{ width: '100%', minWidth: 120 }}>
      <option value="">— (деф. {def})</option>
      {acctList.map((a) => (<option key={a.id} value={a.code}>{a.code} — {a.name}</option>))}
    </select>
  );

  function validate(): string | null {
    if (!form.warehouse_id) return 'Оберіть склад';
    if (form.products.length === 0) return 'Додайте хоча б один рядок продукції';
    if (form.products.some((p) => !p.nomenclature_id))
      return 'Оберіть номенклатуру у всіх рядках продукції';
    return null;
  }

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий звіт виробництва' : `Звіт виробництва: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено ({docQuery.data?.total_cost ?? '0'})
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/production-reports')}
          onSave={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            (isNew ? create.mutate() : update.mutate());
          }}
          saveDisabled={!canCreate || create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          postable={canPost}
          onPost={() => {
            const err = validate();
            if (err) { setError(err); return; }
            setError(null);
            (isNew ? createAndPost.mutate() : updateAndPost.mutate());
          }}
          postPending={create.isPending || update.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={canDelete}
          onDelete={() => remove.mutate()}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.code} — {w.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Підрозділ</label>
          <select value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {deptQ.data?.items.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
      </div>

      {/* Табличні частини — вкладки (як у 1С) */}
      <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.25rem', borderBottom: '2px solid #e2e8f0' }}>
        <button onClick={() => setTab('products')} style={tabBtn(tab === 'products')}>
          Продукція ({form.products.length})
        </button>
        <button onClick={() => setTab('materials')} style={tabBtn(tab === 'materials')}>
          Матеріали ({form.materials.length})
        </button>
        <button onClick={() => setTab('waste')} style={tabBtn(tab === 'waste')}>
          Поворотні відходи ({form.returnable_waste.length})
        </button>
      </div>

      {tab === 'products' && (
        <div style={{ marginTop: '0.75rem' }}>
          <table style={tblStyle}>
            <thead>
              <tr style={theadStyle}>
                <th style={{ ...td, width: 32 }}>#</th>
                <th style={td}>Продукція</th>
                <th style={{ ...td, width: 90 }}>К-сть</th>
                <th style={{ ...td, width: 150 }}>Рах. обліку</th>
                <th style={{ ...td, width: 150 }}>Рах. витрат</th>
                <th style={{ ...td, width: 170 }}>Номенкл. група</th>
                {isWip && <th style={{ ...td, width: 100 }}>Планова ціна</th>}
                {isWip && <th style={{ ...td, width: 100 }}>Планова сума</th>}
                <th style={{ ...td, width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.products.map((p, i) => (
                <tr key={i} style={rowStyle}>
                  <td style={td}>{p.line_number}</td>
                  <td style={td}>
                    <select value={p.nomenclature_id} onChange={(e) => setProductField(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                      <option value="">—</option>
                      {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                    </select>
                  </td>
                  <td style={td}><input type="number" step="0.001" value={p.quantity} onChange={(e) => setProductField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td style={td}>{acctSelect(p.account_code, (v) => setProductField(i, { account_code: v }), '26')}</td>
                  <td style={td}>{acctSelect(p.cost_account_code, (v) => setProductField(i, { cost_account_code: v }), '231')}</td>
                  <td style={td}>
                    <select value={p.nomenclature_group_id ?? ''} onChange={(e) => setProductField(i, { nomenclature_group_id: e.target.value || null })} disabled={isPosted}>
                      <option value="">—</option>
                      {groupQ.data?.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
                    </select>
                  </td>
                  {isWip && <td style={td}><input type="number" step="0.01" value={p.planned_cost ?? ''} onChange={(e) => setProductField(i, { planned_cost: e.target.value || null })} disabled={isPosted} /></td>}
                  {isWip && <td style={td}><input type="number" step="0.01" value={p.planned_amount ?? ''} onChange={(e) => setProductField(i, { planned_amount: e.target.value || null })} disabled={isPosted} /></td>}
                  <td style={td}>{!isPosted && <button onClick={() => removeProduct(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isPosted && <button onClick={addProduct} style={{ marginTop: '0.5rem' }}>+ додати рядок</button>}
        </div>
      )}

      {tab === 'materials' && (
        <div style={{ marginTop: '0.75rem' }}>
          <table style={tblStyle}>
            <thead>
              <tr style={theadStyle}>
                <th style={{ ...td, width: 32 }}>#</th>
                <th style={td}>Сировина</th>
                <th style={{ ...td, width: 90 }}>К-сть</th>
                <th style={{ ...td, width: 150 }}>Рахунок</th>
                <th style={{ ...td, width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.materials.map((m, i) => (
                <tr key={i} style={rowStyle}>
                  <td style={td}>{m.line_number}</td>
                  <td style={td}>
                    <select value={m.nomenclature_id} onChange={(e) => setMaterialField(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                      <option value="">—</option>
                      {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                    </select>
                  </td>
                  <td style={td}><input type="number" step="0.001" value={m.quantity} onChange={(e) => setMaterialField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td style={td}>{acctSelect(m.account_code, (v) => setMaterialField(i, { account_code: v }), '201')}</td>
                  <td style={td}>{!isPosted && <button onClick={() => removeMaterial(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isPosted && <button onClick={addMaterial} style={{ marginTop: '0.5rem' }}>+ додати рядок</button>}
        </div>
      )}

      {tab === 'waste' && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ color: 'var(--muted)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
            Поворотні відходи повертаються на склад і зменшують собівартість продукції на вказану суму.
          </div>
          <table style={tblStyle}>
            <thead>
              <tr style={theadStyle}>
                <th style={{ ...td, width: 32 }}>#</th>
                <th style={td}>Відходи</th>
                <th style={{ ...td, width: 90 }}>К-сть</th>
                <th style={{ ...td, width: 90 }}>Ціна</th>
                <th style={{ ...td, width: 90 }}>Сума</th>
                <th style={{ ...td, width: 150 }}>Рахунок</th>
                <th style={{ ...td, width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {form.returnable_waste.map((w, i) => (
                <tr key={i} style={rowStyle}>
                  <td style={td}>{w.line_number}</td>
                  <td style={td}>
                    <select value={w.nomenclature_id} onChange={(e) => setWasteField(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                      <option value="">—</option>
                      {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                    </select>
                  </td>
                  <td style={td}><input type="number" step="0.001" value={w.quantity} onChange={(e) => setWasteField(i, { quantity: e.target.value })} disabled={isPosted} /></td>
                  <td style={td}><input type="number" step="0.01" value={w.price ?? ''} onChange={(e) => setWasteField(i, { price: e.target.value || null })} disabled={isPosted} /></td>
                  <td style={td}><input type="number" step="0.01" value={w.amount} onChange={(e) => setWasteField(i, { amount: e.target.value })} disabled={isPosted} /></td>
                  <td style={td}>{acctSelect(w.account_code, (v) => setWasteField(i, { account_code: v }), '201')}</td>
                  <td style={td}>{!isPosted && <button onClick={() => removeWaste(i)} title="Видалити рядок">×</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isPosted && <button onClick={addWaste} style={{ marginTop: '0.5rem' }}>+ додати рядок</button>}
        </div>
      )}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
